import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styles from './Settings.module.css';
import { userService, sessionService, authService, homeownerBillingService } from '../../services/api';
import LoginHistory from '../../components/LoginHistory/LoginHistory';
import ActiveSessions from '../../components/common/ActiveSessions/ActiveSessions';
import TwoFactorSetup from '../../components/common/TwoFactorSetup/TwoFactorSetup';
import UsageAnalytics from '../../components/common/UsageAnalytics/UsageAnalytics';

const Settings = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get active tab from URL params, location state, or default to ai-preferences
  const getInitialTab = () => {
    return searchParams.get('tab') || location.state?.activeTab || 'ai-preferences';
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab());
  const [isLoading, setIsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const [showLoginHistory, setShowLoginHistory] = useState(false);
  const [showActiveSessions, setShowActiveSessions] = useState(false);
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  
  // User plan detection from subscription API
  const [userPlan, setUserPlan] = useState('free'); // free, essential, complete, expert
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  
  // Two-Factor Authentication status
  const [twoFactorStatus, setTwoFactorStatus] = useState({
    enabled: false,
    method: null,
    setupDate: null
  });

  // Password expiry tracking
  const [passwordExpiry, setPasswordExpiry] = useState({
    lastChanged: null,
    daysLeft: 180,
    isExpired: false
  });
  
  // Update active tab when URL params or location state changes
  useEffect(() => {
    const newTab = searchParams.get('tab') || location.state?.activeTab;
    if (newTab && newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [searchParams, location.state, activeTab]);

  // Fetch 2FA status, password expiry, and subscription on component mount
  useEffect(() => {
    fetchTwoFactorStatus();
    fetchPasswordExpiry();
    fetchUserSubscription();
  }, []);

  // Update AI and analysis settings when userPlan changes
  useEffect(() => {
    if (!subscriptionLoading && userPlan) {
      const planData = planFeatures[userPlan];
      if (planData) {
        setAiSettings(prev => ({
          ...prev,
          analysisDepth: planData.analysisDepth,
          autoGenerateInsights: planData.features.includes('autoGenerateInsights') || userPlan === 'free',
          enableRiskAnalysis: planData.features.includes('enableRiskAnalysis'),
          enableCostBreakdown: planData.features.includes('enableCostBreakdown'),
          enableTimelineEvaluation: planData.features.includes('enableTimelineEvaluation'),
          enableMissingElementsDetection: planData.features.includes('enableMissingElementsDetection'),
          enableNegotiationGuide: planData.features.includes('enableNegotiationGuide'),
          enableProjectManagement: planData.features.includes('enableProjectManagement'),
          enableAIProposalGeneration: planData.features.includes('enableAIProposalGeneration')
        }));

        setAnalysisSettings(prev => ({
          ...prev,
          enableContractorScoring: planData.features.includes('enableContractorScoring'),
          enableBehaviorAnalysis: planData.features.includes('enableBehaviorAnalysis'),
          includeHiddenCosts: planData.features.includes('includeHiddenCosts'),
          generatePDFReports: planData.features.includes('generatePDFReports'),
          enableCustomQuestions: planData.features.includes('enableCustomQuestions'),
          enableAdvancedRiskModeling: planData.features.includes('enableAdvancedRiskModeling')
        }));
      }
    }
  }, [userPlan, subscriptionLoading]);

  const fetchTwoFactorStatus = async () => {
    try {
      const response = await authService.getTwoFactorStatus();
      if (response.data.status === 'success') {
        const status = response.data.twoFactorStatus;
        setTwoFactorStatus(status);
        // Update the checkbox to match the actual 2FA status
        setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: status.enabled }));
      }
    } catch (error) {
      console.error('Error fetching 2FA status:', error);
    }
  };

  const fetchPasswordExpiry = async () => {
    try {
      // Get from localStorage user data (updated on login)
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log(user, 'user');
      const lastChanged = user.passwordLastChanged;
      console.log(lastChanged, 'lastChanged');
      
      if (lastChanged) {
        calculatePasswordExpiry(lastChanged);
      } else {
        // If no password change date, assume it was set when account was created
        // For existing users without this field, set to current date
        const defaultDate = new Date().toISOString();
        console.log('No passwordLastChanged found, using current date:', defaultDate);
        calculatePasswordExpiry(defaultDate);
      }
    } catch (error) {
      console.error('Error fetching password expiry:', error);
      // Default to current date if error
      calculatePasswordExpiry(new Date().toISOString());
    }
  };

  const fetchUserSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const response = await homeownerBillingService.getSubscription();
      
      if (response.data.status === 'success') {
        const subscriptionData = response.data.data;
        
        if (subscriptionData && subscriptionData.status === 'active') {
          // Map plan name to lowercase format
          const planName = subscriptionData.plan_name.toLowerCase();
          setUserPlan(planName);
          console.log('User subscription found:', planName);
        } else {
          // No active subscription found, user is on free plan
          setUserPlan('free');
          console.log('No active subscription, user is on free plan');
        }
      } else {
        // API error, default to free plan
        setUserPlan('free');
        console.log('API error, defaulting to free plan');
      }
    } catch (error) {
      console.error('Error fetching user subscription:', error);
      // On error, default to free plan
      setUserPlan('free');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const calculatePasswordExpiry = (lastChangedDate) => {
    const lastChanged = new Date(lastChangedDate);
    const now = new Date();
    const expiryDate = new Date(lastChanged);
    expiryDate.setDate(expiryDate.getDate() + 180); // 180 days from last change
    
    const timeDiff = expiryDate.getTime() - now.getTime();
    const daysLeft = Math.floor(timeDiff / (1000 * 3600 * 24));
    
    setPasswordExpiry({
      lastChanged: lastChanged,
      daysLeft: Math.max(0, daysLeft),
      isExpired: daysLeft <= 0
    });
  };

  // Function to handle tab changes and update URL
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // Plan feature mapping
  const planFeatures = {
    free: {
      analysisDepth: 'basic',
      maxContractors: 2,
      fileStorage: '250MB',
      features: [] // Only allowDataForTraining is available for free users
    },
    essential: {
      analysisDepth: 'essential',
      maxContractors: 3,
      fileStorage: 'none',
      features: ['enableRiskAnalysis', 'enableCostBreakdown', 'enableContractorScoring', 'enableBehaviorAnalysis', 'includeHiddenCosts', 'generatePDFReports']
    },
    complete: {
      analysisDepth: 'complete',
      maxContractors: 5,
      fileStorage: '1GB',
      features: ['enableRiskAnalysis', 'enableCostBreakdown', 'enableContractorScoring', 'enableBehaviorAnalysis', 'includeHiddenCosts', 'generatePDFReports', 'enableTimelineEvaluation', 'enableMissingElementsDetection', 'enableCustomQuestions']
    },
    expert: {
      analysisDepth: 'expert',
      maxContractors: 5,
      fileStorage: '5GB',
      features: ['enableRiskAnalysis', 'enableCostBreakdown', 'enableContractorScoring', 'enableBehaviorAnalysis', 'includeHiddenCosts', 'generatePDFReports', 'enableTimelineEvaluation', 'enableMissingElementsDetection', 'enableCustomQuestions', 'enableNegotiationGuide', 'enableProjectManagement', 'enableAIProposalGeneration', 'enableAdvancedRiskModeling']
    }
  };

  // Feature to plan mapping for upgrades
  const featureUpgradeMap = {
    // Features that require Essential plan (for free users)
    enableRiskAnalysis: { requiredPlan: 'essential', planName: 'Essential' },
    enableCostBreakdown: { requiredPlan: 'essential', planName: 'Essential' },
    enableContractorScoring: { requiredPlan: 'essential', planName: 'Essential' },
    enableBehaviorAnalysis: { requiredPlan: 'essential', planName: 'Essential' },
    includeHiddenCosts: { requiredPlan: 'essential', planName: 'Essential' },
    generatePDFReports: { requiredPlan: 'essential', planName: 'Essential' },
    
    // Features that require Complete plan
    enableTimelineEvaluation: { requiredPlan: 'complete', planName: 'Complete' },
    enableMissingElementsDetection: { requiredPlan: 'complete', planName: 'Complete' },
    enableCustomQuestions: { requiredPlan: 'complete', planName: 'Complete' },
    
    // Features that require Expert plan
    enableNegotiationGuide: { requiredPlan: 'expert', planName: 'Expert' },
    enableProjectManagement: { requiredPlan: 'expert', planName: 'Expert' },
    enableAIProposalGeneration: { requiredPlan: 'expert', planName: 'Expert' },
    enableAdvancedRiskModeling: { requiredPlan: 'expert', planName: 'Expert' }
  };

  // AI & Analysis Preferences - Initialize after userPlan is loaded
  const [aiSettings, setAiSettings] = useState({
    allowDataForTraining: true,
    analysisDepth: 'basic',
    autoGenerateInsights: true,
    enableRiskAnalysis: false,
    enableCostBreakdown: false,
    enableTimelineEvaluation: false,
    enableMissingElementsDetection: false,
    enableNegotiationGuide: false,
    enableProjectManagement: false,
    enableAIProposalGeneration: false
  });

  // Privacy & Data Settings
  const [privacySettings, setPrivacySettings] = useState({
    dataRetentionPeriod: '2-years', // 1-year, 2-years, 5-years, indefinite
    allowCookies: true,
    shareAnonymizedData: false,
    enableLocationServices: true,
    deleteDataOnAccountClosure: true,
    gdprComplianceMode: true
  });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    analysisComplete: true,
    riskFlagsDetected: true,
    potentialSavingsFound: true,
    weeklyDigest: false,
    monthlyReport: true,
    securityAlerts: true,
    billingReminders: true,
    productUpdates: false
  });

  // Analysis Preferences - Initialize after userPlan is loaded
  const [analysisSettings, setAnalysisSettings] = useState({
    enableContractorScoring: false,
    enableBehaviorAnalysis: false,
    includeHiddenCosts: false,
    generatePDFReports: false,
    enableCustomQuestions: false,
    enableAdvancedRiskModeling: false
  });

  // Security Settings
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: false,
    passwordExpiry: '90-days', // never, 30-days, 60-days, 90-days
    sessionTimeout: '24-hours', // 1-hour, 8-hours, 24-hours, never
    loginNotifications: true,
    deviceTracking: true
  });

  // Check if feature is available in current plan
  const isFeatureAvailable = (featureKey) => {
    return planFeatures[userPlan].features.includes(featureKey);
  };

  // Handle feature toggle with upgrade check
  const handleFeatureToggle = (featureKey, currentValue, setter) => {
    if (!isFeatureAvailable(featureKey) && !currentValue) {
      // Feature not available in current plan, show upgrade modal
      const upgradeInfo = featureUpgradeMap[featureKey];
      
      // Check if upgrade info exists for this feature
      if (upgradeInfo) {
        setUpgradeFeature({
          feature: featureKey,
          requiredPlan: upgradeInfo.requiredPlan,
          planName: upgradeInfo.planName
        });
        setShowUpgradeModal(true);
        return;
      }
    }
    
    // Feature is available or user is turning it off, or no upgrade info exists
    setter(prev => ({ ...prev, [featureKey]: !currentValue }));
  };

  // Handle clicking on disabled premium sections
  const handlePremiumSectionClick = (featureKey) => {
    if (!isFeatureAvailable(featureKey)) {
      const upgradeInfo = featureUpgradeMap[featureKey];
      if (upgradeInfo) {
        setUpgradeFeature({
          feature: featureKey,
          requiredPlan: upgradeInfo.requiredPlan,
          planName: upgradeInfo.planName
        });
        setShowUpgradeModal(true);
      }
    }
  };

  const handleAiChange = (key, value) => {
    if (typeof value === 'boolean') {
      // Only use feature toggle logic for premium features
      if (featureUpgradeMap[key]) {
        handleFeatureToggle(key, aiSettings[key], setAiSettings);
      } else {
        // Basic feature, just toggle normally
        setAiSettings(prev => ({ ...prev, [key]: !aiSettings[key] }));
      }
    } else {
      setAiSettings(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleAnalysisChange = (key, value) => {
    if (typeof value === 'boolean') {
      // Only use feature toggle logic for premium features
      if (featureUpgradeMap[key]) {
        handleFeatureToggle(key, analysisSettings[key], setAnalysisSettings);
      } else {
        // Basic feature, just toggle normally
        setAnalysisSettings(prev => ({ ...prev, [key]: !analysisSettings[key] }));
      }
    } else {
      setAnalysisSettings(prev => ({ ...prev, [key]: value }));
    }
  };

  const handlePrivacyChange = (key, value) => {
    setPrivacySettings(prev => ({ ...prev, [key]: value }));
  };

  const handleNotificationChange = (key) => {
    setNotificationSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSecurityChange = (key, value) => {
    setSecuritySettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveSettings = async () => {
    setIsLoading(true);
    try {
      // Here you would make API calls to save settings
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setSaveStatus('Settings saved successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      setSaveStatus('Error saving settings. Please try again.');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestDataExport = () => {
    alert('Your data export request has been submitted. You will receive an email with your data within 48 hours.');
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      alert('Account deletion request submitted. Please check your email for confirmation.');
    }
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(false);
    // Redirect to homeowner pricing page with selected plan
    navigate(`/homeowner/pricing?upgrade=${upgradeFeature.requiredPlan}`);
  };

  const handleChangePassword = () => {
    // Store callback to reset password expiry after successful change
    sessionStorage.setItem('passwordChangeCallback', 'true');
    navigate('/change-password');
  };

  // Listen for password change completion
  useEffect(() => {
    const handlePasswordChanged = () => {
      if (sessionStorage.getItem('passwordChangeCallback') === 'true') {
        sessionStorage.removeItem('passwordChangeCallback');
        
        // Reset password expiry
        const newPasswordDate = new Date().toISOString();
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.passwordLastChanged = newPasswordDate;
        localStorage.setItem('user', JSON.stringify(user));
        
        calculatePasswordExpiry(newPasswordDate);
      }
    };

    // Check on component focus (when user returns from change password page)
    window.addEventListener('focus', handlePasswordChanged);
    return () => window.removeEventListener('focus', handlePasswordChanged);
  }, []);

  const handleViewLoginHistory = () => {
    setShowLoginHistory(true);
  };

  const handleViewActiveSessions = () => {
    setShowActiveSessions(true);
  };

  const handleLogoutAllDevices = async () => {
    if (!window.confirm('Are you sure you want to sign out from all other devices? You will remain logged in on this device.')) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await sessionService.revokeAllOtherSessions();
      
      if (response.data.status === 'success') {
        alert(`Successfully signed out from all other devices. ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error logging out from all devices:', error);
      alert('Failed to sign out from all devices. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetupTwoFactor = () => {
    setShowTwoFactorSetup(true);
  };

  const handleDisableTwoFactor = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      // User cancelled, revert the checkbox by updating securitySettings
      setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: true }));
      return;
    }

    try {
      setIsLoading(true);
      const response = await authService.disableTwoFactor(twoFactorStatus.method);
      if (response.data.status === 'success') {
        setTwoFactorStatus({ enabled: false, method: null, setupDate: null });
        setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: false }));
        setSaveStatus('Two-factor authentication has been disabled');
        setTimeout(() => setSaveStatus(''), 3000);
      }
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      setSaveStatus('Error: Failed to disable two-factor authentication');
      setTimeout(() => setSaveStatus(''), 3000);
      // Revert checkbox state on error
      setSecuritySettings(prev => ({ ...prev, twoFactorEnabled: true }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleTwoFactorSetupComplete = () => {
    setShowTwoFactorSetup(false);
    fetchTwoFactorStatus(); // Refresh 2FA status
  };

  // Function to refresh subscription data (can be called from other components)
  const refreshSubscription = () => {
    fetchUserSubscription();
  };

  // Listen for subscription changes (e.g., when user upgrades)
  useEffect(() => {
    const handleSubscriptionUpdate = () => {
      fetchUserSubscription();
    };

    // Listen for custom events
    window.addEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    
    return () => {
      window.removeEventListener('subscriptionUpdated', handleSubscriptionUpdate);
    };
  }, []);

  const getPlanDisplayName = (plan) => {
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  return (
    <div className={styles.settingsWrapper}>
      <div className={styles.settingsHeader}>
        <div className={styles.headerContent}>
          <h1>Settings</h1>
          <p>Configure your Trakvu preferences and analysis settings</p>
          <div className={styles.currentPlan}>
            <span className={styles.planBadge}>Current Plan: {getPlanDisplayName(userPlan)}</span>
          </div>
        </div>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.upgradeModal}>
            <div className={styles.modalHeader}>
              <h3>Upgrade Required</h3>
              <button 
                className={styles.closeModal}
                onClick={() => setShowUpgradeModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className={styles.modalContent}>
              <div className={styles.upgradeIcon}>
                <i className="fas fa-crown"></i>
              </div>
              <h4>This feature requires the {upgradeFeature?.planName} plan</h4>
              <p>Upgrade your plan to unlock this premium feature and get access to advanced AI analysis capabilities.</p>
              <div className={styles.modalActions}>
                <button 
                  className={styles.upgradeButton}
                  onClick={handleUpgrade}
                >
                  <i className="fas fa-arrow-up"></i>
                  Upgrade to {upgradeFeature?.planName}
                </button>
                <button 
                  className={styles.cancelButton}
                  onClick={() => setShowUpgradeModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.settingsContainer}>
        <div className={styles.sidebar}>
          <button 
            className={`${styles.tabButton} ${activeTab === 'ai-preferences' ? styles.active : ''}`}
            onClick={() => handleTabChange('ai-preferences')}
          >
            <i className="fas fa-brain"></i>
            AI Preferences
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'analysis' ? styles.active : ''}`}
            onClick={() => handleTabChange('analysis')}
          >
            <i className="fas fa-chart-line"></i>
            Analysis Settings
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'notifications' ? styles.active : ''}`}
            onClick={() => handleTabChange('notifications')}
          >
            <i className="fas fa-bell"></i>
            Notifications
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'privacy' ? styles.active : ''}`}
            onClick={() => handleTabChange('privacy')}
          >
            <i className="fas fa-shield-alt"></i>
            Privacy & Data
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'security' ? styles.active : ''}`}
            onClick={() => handleTabChange('security')}
          >
            <i className="fas fa-lock"></i>
            Security
          </button>
          <button 
            className={`${styles.tabButton} ${activeTab === 'usage' ? styles.active : ''}`}
            onClick={() => handleTabChange('usage')}
          >
            <i className="fas fa-chart-bar"></i>
            Usage Analytics
          </button>
        </div>

        <div className={styles.mainContent}>
          {saveStatus && (
            <div className={`${styles.saveStatus} ${saveStatus.includes('Error') ? styles.error : styles.success}`}>
              {saveStatus}
            </div>
          )}

          {subscriptionLoading && (
            <div className={styles.loadingState}>
              <i className="fas fa-spinner fa-spin"></i>
              <span>Loading subscription information...</span>
            </div>
          )}

          {activeTab === 'ai-preferences' && !subscriptionLoading && (
            <div className={styles.section}>
              <h2>AI & Analysis Preferences</h2>
              
              {userPlan === 'free' && (
                <div className={styles.freeUserBanner}>
                  <div className={styles.bannerContent}>
                    <i className="fas fa-gift"></i>
                    <div>
                      <h3>Free Plan - Limited Features</h3>
                      <p>You're currently on the Free plan. Upgrade to unlock powerful AI analysis features!</p>
                    </div>
                    <button 
                      className={styles.upgradeButton}
                      onClick={() => setShowUpgradeModal(true)}
                    >
                      View Plans
                    </button>
                  </div>
                </div>
              )}
              
              <div className={styles.aiPreferencesGrid}>
                <div className={styles.preferenceCard}>
                  <div className={styles.cardHeader}>
                    <i className="fas fa-database"></i>
                    <h3>Data Usage for AI Training</h3>
                  </div>
                  <p>Help improve Trakvu's AI by allowing your data to be used for training our models.</p>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={aiSettings.allowDataForTraining}
                      onChange={() => handleAiChange('allowDataForTraining', !aiSettings.allowDataForTraining)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.preferenceCard}>
                  <div className={styles.cardHeader}>
                    <i className="fas fa-microscope"></i>
                    <h3>Analysis Depth</h3>
                  </div>
                  <p>Choose how detailed you want your proposal analysis to be.</p>
                  <select 
                    value={aiSettings.analysisDepth}
                    onChange={(e) => handleAiChange('analysisDepth', e.target.value)}
                    className={styles.preferenceSelect}
                    disabled={userPlan === 'free'}
                  >
                    <option value="basic">Basic - Simple comparison</option>
                    <option value="essential" disabled={userPlan === 'free'}>
                      Essential - Quick overview {userPlan === 'free' && '(Essential+ Required)'}
                    </option>
                    <option value="complete" disabled={userPlan === 'free' || userPlan === 'essential'}>
                      Complete - Balanced analysis {(userPlan === 'free' || userPlan === 'essential') && '(Complete+ Required)'}
                    </option>
                    <option value="expert" disabled={userPlan !== 'expert'}>
                      Expert - Deep insights {userPlan !== 'expert' && '(Expert Plan Required)'}
                    </option>
                  </select>
                </div>

                <div 
                  className={`${styles.preferenceCard} ${!isFeatureAvailable('enableRiskAnalysis') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableRiskAnalysis')}
                  style={{ cursor: !isFeatureAvailable('enableRiskAnalysis') ? 'pointer' : 'default' }}
                >
                  <div className={styles.cardHeader}>
                    <i className="fas fa-shield-alt"></i>
                    <h3>
                      Risk Analysis
                      {!isFeatureAvailable('enableRiskAnalysis') && (
                        <span className={styles.premiumBadge}>Essential+</span>
                      )}
                    </h3>
                  </div>
                  <p>Enable risk analysis for homeowner and project protection.</p>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableRiskAnalysis') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={aiSettings.enableRiskAnalysis}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAiChange('enableRiskAnalysis', !aiSettings.enableRiskAnalysis);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.preferenceCard} ${!isFeatureAvailable('enableCostBreakdown') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableCostBreakdown')}
                  style={{ cursor: !isFeatureAvailable('enableCostBreakdown') ? 'pointer' : 'default' }}
                >
                  <div className={styles.cardHeader}>
                    <i className="fas fa-calculator"></i>
                    <h3>
                      Cost Breakdown Analysis
                      {!isFeatureAvailable('enableCostBreakdown') && (
                        <span className={styles.premiumBadge}>Essential+</span>
                      )}
                    </h3>
                  </div>
                  <p>Enable detailed cost breakdown and hidden costs detection.</p>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableCostBreakdown') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={aiSettings.enableCostBreakdown}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAiChange('enableCostBreakdown', !aiSettings.enableCostBreakdown);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>

              <div className={styles.settingsList}>
                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableTimelineEvaluation') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableTimelineEvaluation')}
                  style={{ cursor: !isFeatureAvailable('enableTimelineEvaluation') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Timeline Evaluation
                      {!isFeatureAvailable('enableTimelineEvaluation') && (
                        <span className={styles.premiumBadge}>Complete+</span>
                      )}
                    </h3>
                    <p>Include project timeline comparison and scheduling insights (Complete & Expert plans)</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableTimelineEvaluation') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={aiSettings.enableTimelineEvaluation}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAiChange('enableTimelineEvaluation', !aiSettings.enableTimelineEvaluation);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableMissingElementsDetection') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableMissingElementsDetection')}
                  style={{ cursor: !isFeatureAvailable('enableMissingElementsDetection') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Missing Elements Detection
                      {!isFeatureAvailable('enableMissingElementsDetection') && (
                        <span className={styles.premiumBadge}>Complete+</span>
                      )}
                    </h3>
                    <p>AI identifies what contractors forgot to include in their proposals (Complete & Expert plans)</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableMissingElementsDetection') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={aiSettings.enableMissingElementsDetection}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAiChange('enableMissingElementsDetection', !aiSettings.enableMissingElementsDetection);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableNegotiationGuide') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableNegotiationGuide')}
                  style={{ cursor: !isFeatureAvailable('enableNegotiationGuide') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Negotiation Strategy Guide
                      {!isFeatureAvailable('enableNegotiationGuide') && (
                        <span className={styles.premiumBadge}>Expert</span>
                      )}
                    </h3>
                    <p>AI-powered negotiation tactics based on contractor weaknesses (Expert plan only)</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableNegotiationGuide') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={aiSettings.enableNegotiationGuide}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAiChange('enableNegotiationGuide', !aiSettings.enableNegotiationGuide);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableProjectManagement') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableProjectManagement')}
                  style={{ cursor: !isFeatureAvailable('enableProjectManagement') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Project Management Tools
                      {!isFeatureAvailable('enableProjectManagement') && (
                        <span className={styles.premiumBadge}>Expert</span>
                      )}
                    </h3>
                    <p>Complete project tracking with milestones and progress monitoring (Expert plan only)</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableProjectManagement') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={aiSettings.enableProjectManagement}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAiChange('enableProjectManagement', !aiSettings.enableProjectManagement);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableAIProposalGeneration') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableAIProposalGeneration')}
                  style={{ cursor: !isFeatureAvailable('enableAIProposalGeneration') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      AI-Powered Proposal Generation
                      {!isFeatureAvailable('enableAIProposalGeneration') && (
                        <span className={styles.premiumBadge}>Expert</span>
                      )}
                    </h3>
                    <p>Generate counter-proposals and scope modifications using AI (Expert plan only)</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableAIProposalGeneration') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={aiSettings.enableAIProposalGeneration}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAiChange('enableAIProposalGeneration', !aiSettings.enableAIProposalGeneration);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analysis' && !subscriptionLoading && (
            <div className={styles.section}>
              <h2>Analysis & Comparison Settings</h2>
              <div className={styles.planInfoGrid}>
                <div className={`${styles.planInfoCard} ${userPlan === 'free' ? styles.freePlan : ''}`}>
                  <div className={styles.planInfoHeader}>
                    <i className="fas fa-users"></i>
                    <h3>Contractor Limit</h3>
                  </div>
                  <p className={styles.planValue}>
                    {userPlan === 'free' && 'Up to 2 contractors'}
                    {userPlan === 'essential' && 'Up to 3 contractors'}
                    {(userPlan === 'complete' || userPlan === 'expert') && 'Up to 5 contractors'}
                  </p>
                  <p className={styles.planDescription}>Based on your {getPlanDisplayName(userPlan)} plan</p>
                </div>

                <div className={`${styles.planInfoCard} ${userPlan === 'free' ? styles.freePlan : ''}`}>
                  <div className={styles.planInfoHeader}>
                    <i className="fas fa-cloud"></i>
                    <h3>File Storage</h3>
                  </div>
                  <p className={styles.planValue}>
                    {userPlan === 'free' && '250MB storage included'}
                    {userPlan === 'essential' && '250MB storage included'}
                    {userPlan === 'complete' && '1GB storage included'}
                    {userPlan === 'expert' && '5GB storage included'}
                  </p>
                  <p className={styles.planDescription}>
                    {userPlan === 'free' && 'Perfect for getting started'}
                    {userPlan === 'essential' && 'Upgrade for file storage'}
                    {(userPlan === 'complete' || userPlan === 'expert') && 'Store contractor documents'}
                  </p>
                </div>
              </div>

              <div className={styles.settingsList}>
                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableContractorScoring') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableContractorScoring')}
                  style={{ cursor: !isFeatureAvailable('enableContractorScoring') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Contractor Scoring System
                      {!isFeatureAvailable('enableContractorScoring') && (
                        <span className={styles.premiumBadge}>Essential+</span>
                      )}
                    </h3>
                    <p>Comprehensive scoring across multiple categories for each contractor</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableContractorScoring') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={analysisSettings.enableContractorScoring}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAnalysisChange('enableContractorScoring', !analysisSettings.enableContractorScoring);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableBehaviorAnalysis') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableBehaviorAnalysis')}
                  style={{ cursor: !isFeatureAvailable('enableBehaviorAnalysis') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Contractor Behavior Analysis
                      {!isFeatureAvailable('enableBehaviorAnalysis') && (
                        <span className={styles.premiumBadge}>Essential+</span>
                      )}
                    </h3>
                    <p>Smart questions to evaluate contractor communication and reliability</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableBehaviorAnalysis') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={analysisSettings.enableBehaviorAnalysis}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAnalysisChange('enableBehaviorAnalysis', !analysisSettings.enableBehaviorAnalysis);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('includeHiddenCosts') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('includeHiddenCosts')}
                  style={{ cursor: !isFeatureAvailable('includeHiddenCosts') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Hidden Costs Detection
                      {!isFeatureAvailable('includeHiddenCosts') && (
                        <span className={styles.premiumBadge}>Essential+</span>
                      )}
                    </h3>
                    <p>Identify potential hidden costs and additional expenses in proposals</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('includeHiddenCosts') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={analysisSettings.includeHiddenCosts}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAnalysisChange('includeHiddenCosts', !analysisSettings.includeHiddenCosts);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('generatePDFReports') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('generatePDFReports')}
                  style={{ cursor: !isFeatureAvailable('generatePDFReports') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      PDF Report Generation
                      {!isFeatureAvailable('generatePDFReports') && (
                        <span className={styles.premiumBadge}>Essential+</span>
                      )}
                    </h3>
                    <p>Generate downloadable PDF reports for your analysis</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('generatePDFReports') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={analysisSettings.generatePDFReports}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAnalysisChange('generatePDFReports', !analysisSettings.generatePDFReports);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableCustomQuestions') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableCustomQuestions')}
                  style={{ cursor: !isFeatureAvailable('enableCustomQuestions') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Custom Contractor Questions
                      {!isFeatureAvailable('enableCustomQuestions') && (
                        <span className={styles.premiumBadge}>Complete+</span>
                      )}
                    </h3>
                    <p>Tailored questions based on your specific project needs (Complete & Expert plans)</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableCustomQuestions') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={analysisSettings.enableCustomQuestions}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAnalysisChange('enableCustomQuestions', !analysisSettings.enableCustomQuestions);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div 
                  className={`${styles.settingItem} ${!isFeatureAvailable('enableAdvancedRiskModeling') ? styles.premiumFeature : ''}`}
                  onClick={() => handlePremiumSectionClick('enableAdvancedRiskModeling')}
                  style={{ cursor: !isFeatureAvailable('enableAdvancedRiskModeling') ? 'pointer' : 'default' }}
                >
                  <div className={styles.settingInfo}>
                    <h3>
                      Advanced Risk Modeling
                      {!isFeatureAvailable('enableAdvancedRiskModeling') && (
                        <span className={styles.premiumBadge}>Expert</span>
                      )}
                    </h3>
                    <p>Advanced risk modeling and timeline simulation (Expert plan only)</p>
                  </div>
                  <label className={`${styles.switch} ${!isFeatureAvailable('enableAdvancedRiskModeling') ? styles.disabled : ''}`}>
                    <input 
                      type="checkbox"
                      checked={analysisSettings.enableAdvancedRiskModeling}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleAnalysisChange('enableAdvancedRiskModeling', !analysisSettings.enableAdvancedRiskModeling);
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && !subscriptionLoading && (
            <div className={styles.section}>
              <h2>Notification Preferences</h2>
              <div className={styles.settingsList}>
                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Email Notifications</h3>
                    <p>Receive notifications via email</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.emailNotifications}
                      onChange={() => handleNotificationChange('emailNotifications')}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Analysis Complete</h3>
                    <p>Get notified when your proposal analysis is ready</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.analysisComplete}
                      onChange={() => handleNotificationChange('analysisComplete')}
                      disabled={!notificationSettings.emailNotifications}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Risk Flags Detected</h3>
                    <p>Immediate alerts when potential risks are found</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.riskFlagsDetected}
                      onChange={() => handleNotificationChange('riskFlagsDetected')}
                      disabled={!notificationSettings.emailNotifications}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Potential Savings Found</h3>
                    <p>Get notified when significant savings are identified</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.potentialSavingsFound}
                      onChange={() => handleNotificationChange('potentialSavingsFound')}
                      disabled={!notificationSettings.emailNotifications}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Weekly Digest</h3>
                    <p>Weekly summary of your projects and insights</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.weeklyDigest}
                      onChange={() => handleNotificationChange('weeklyDigest')}
                      disabled={!notificationSettings.emailNotifications}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Monthly Report</h3>
                    <p>Monthly analysis report with trends and insights</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.monthlyReport}
                      onChange={() => handleNotificationChange('monthlyReport')}
                      disabled={!notificationSettings.emailNotifications}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Security Alerts</h3>
                    <p>Important security notifications and login alerts</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.securityAlerts}
                      onChange={() => handleNotificationChange('securityAlerts')}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Billing Reminders</h3>
                    <p>Payment reminders and billing notifications</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.billingReminders}
                      onChange={() => handleNotificationChange('billingReminders')}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Product Updates</h3>
                    <p>News about new features and platform updates</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={notificationSettings.productUpdates}
                      onChange={() => handleNotificationChange('productUpdates')}
                      disabled={!notificationSettings.emailNotifications}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'privacy' && !subscriptionLoading && (
            <div className={styles.section}>
              <h2>Privacy & Data Management</h2>
              
              <div className={styles.privacySection}>
                <div className={styles.privacyCard}>
                  <div className={styles.cardHeader}>
                    <i className="fas fa-file-contract"></i>
                    <h3>Proposal Data Retention</h3>
                  </div>
                  <p>How long to keep your contractor proposals and analysis data.</p>
                  <select 
                    value={privacySettings.dataRetentionPeriod}
                    onChange={(e) => handlePrivacyChange('dataRetentionPeriod', e.target.value)}
                    className={styles.preferenceSelect}
                  >
                    <option value="1-year">1 Year (Recommended)</option>
                    <option value="2-years">2 Years</option>
                    <option value="5-years">5 Years</option>
                    <option value="indefinite">Keep Indefinitely</option>
                  </select>
                </div>

                <div className={styles.privacyCard}>
                  <div className={styles.cardHeader}>
                    <i className="fas fa-shield-alt"></i>
                    <h3>Data Protection Mode</h3>
                  </div>
                  <p>Enhanced privacy controls for sensitive project information.</p>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={privacySettings.gdprComplianceMode}
                      onChange={() => handlePrivacyChange('gdprComplianceMode', !privacySettings.gdprComplianceMode)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>

              <div className={styles.settingsList}>
                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Contractor Information Sharing</h3>
                    <p>Allow sharing of contractor performance data (anonymized) to improve platform insights</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={privacySettings.shareAnonymizedData}
                      onChange={() => handlePrivacyChange('shareAnonymizedData', !privacySettings.shareAnonymizedData)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Location-Based Analysis</h3>
                    <p>Use your location for regional contractor pricing and market insights</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={privacySettings.enableLocationServices}
                      onChange={() => handlePrivacyChange('enableLocationServices', !privacySettings.enableLocationServices)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>AI Model Training Consent</h3>
                    <p>Allow your proposal data to help train and improve our AI analysis models</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={privacySettings.allowCookies}
                      onChange={() => handlePrivacyChange('allowCookies', !privacySettings.allowCookies)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Automatic Data Deletion</h3>
                    <p>Permanently delete all proposals, analysis, and contractor data when account is closed</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={privacySettings.deleteDataOnAccountClosure}
                      onChange={() => handlePrivacyChange('deleteDataOnAccountClosure', !privacySettings.deleteDataOnAccountClosure)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Contractor Contact Privacy</h3>
                    <p>Prevent contractors from seeing your contact information during comparisons</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={privacySettings.contractorContactPrivacy || false}
                      onChange={() => handlePrivacyChange('contractorContactPrivacy', !privacySettings.contractorContactPrivacy)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Analysis History Privacy</h3>
                    <p>Keep your project analysis history private from shared device users</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={privacySettings.analysisHistoryPrivacy || false}
                      onChange={() => handlePrivacyChange('analysisHistoryPrivacy', !privacySettings.analysisHistoryPrivacy)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>

              <div className={styles.dataActions}>
                <h3>Your Data Rights</h3>
                <div className={styles.actionButtons}>
                  <button 
                    className={styles.exportButton}
                    onClick={handleRequestDataExport}
                  >
                    <i className="fas fa-download"></i>
                    Export All Project Data
                  </button>
                  <button 
                    className={styles.viewButton}
                    onClick={() => alert('Feature coming soon: View all stored contractor and proposal data')}
                  >
                    <i className="fas fa-eye"></i>
                    View Stored Data
                  </button>
                  <button 
                    className={styles.deleteButton}
                    onClick={handleDeleteAccount}
                  >
                    <i className="fas fa-trash"></i>
                    Delete Account & Data
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && !subscriptionLoading && (
            <div className={styles.section}>
              <h2>Security Settings</h2>
              <div className={styles.securityGrid}>
                <div className={styles.securityCard}>
                  <div className={styles.cardHeader}>
                    <i className="fas fa-mobile-alt"></i>
                    <h3>Two-Factor Authentication</h3>
                  </div>
                  <p>Add an extra layer of security to your account.</p>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={twoFactorStatus.enabled}
                      onChange={(e) => {
                        if (e.target.checked) {
                          // User wants to enable 2FA - open setup modal
                          handleSetupTwoFactor();
                        } else {
                          // User wants to disable 2FA
                          handleDisableTwoFactor();
                        }
                      }}
                    />
                    <span className={styles.slider}></span>
                  </label>
                  {twoFactorStatus.enabled ? (
                    <div className={styles.twoFactorConfigured}>
                      <div className={styles.configuredInfo}>
                        <p>
                          <strong>Method:</strong> {twoFactorStatus.method === 'email' && 'Email Authentication'}
                          {twoFactorStatus.method === 'totp' && 'Google Authenticator (TOTP)'}
                        </p>
                        {twoFactorStatus.setupDate && (
                          <p className={styles.setupDate}>
                            <strong>Enabled:</strong> {new Date(twoFactorStatus.setupDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <button 
                        className={styles.configureButton}
                        onClick={handleSetupTwoFactor}
                      >
                        <i className="fas fa-cog"></i>
                        Reconfigure 2FA
                      </button>
                    </div>
                  ) : null}
                  {/* Show configure button if 2FA is enabled in settings but not actually configured */}
                  {securitySettings.twoFactorEnabled && !twoFactorStatus.enabled ? (
                    <button 
                      className={styles.configureButton}
                      onClick={handleSetupTwoFactor}
                    >
                      <i className="fas fa-cog"></i>
                      Configure 2FA
                    </button>
                  ) : null}
                </div>

                <div className={styles.securityCard}>
                  <div className={styles.cardHeader}>
                    <i className="fas fa-clock"></i>
                    <h3>Session Timeout</h3>
                  </div>
                  <p>Sessions automatically expire after 24 hours for security.</p>
                  
                  <div className={styles.sessionTimeoutInfo}>
                    <div className={styles.timeoutDisplay}>
                      <span className={styles.timeoutNumber}>24</span>
                      <span className={styles.timeoutLabel}>hours</span>
                    </div>
                    <div className={styles.timeoutDetails}>
                      <p>Your session will automatically end after 24 hours of inactivity to keep your account secure.</p>
                    </div>
                  </div>
                </div>

                <div className={styles.securityCard}>
                  <div className={styles.cardHeader}>
                    <i className="fas fa-key"></i>
                    <h3>Password Expiry</h3>
                  </div>
                  <p>Passwords must be changed every 180 days for security.</p>
                  
                  <div className={styles.passwordExpiryInfo}>
                    {passwordExpiry.isExpired ? (
                      <div className={styles.expiredWarning}>
                        <i className="fas fa-exclamation-triangle"></i>
                        <div>
                          <strong>Password Expired!</strong>
                          <p>Your password has expired. You must change it to continue using your account.</p>
                        </div>
                      </div>
                    ) : (
                      <div className={styles.expiryCountdown}>
                        <div className={styles.countdownDisplay}>
                          <span className={styles.daysNumber}>{passwordExpiry.daysLeft}</span>
                          <span className={styles.daysLabel}>days left</span>
                        </div>
                        <div className={styles.expiryDetails}>
                          <p>
                            <strong>Last changed:</strong> {' '}
                            {passwordExpiry.lastChanged 
                              ? new Date(passwordExpiry.lastChanged).toLocaleDateString()
                              : 'Unknown'
                            }
                          </p>
                          {passwordExpiry.daysLeft <= 30 && (
                            <div className={styles.warningMessage}>
                              <i className="fas fa-exclamation-circle"></i>
                              <span>Consider changing your password soon</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.settingsList}>
                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Login Notifications</h3>
                    <p>Get notified of new login attempts from unrecognized devices</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={securitySettings.loginNotifications}
                      onChange={() => handleSecurityChange('loginNotifications', !securitySettings.loginNotifications)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>

                <div className={styles.settingItem}>
                  <div className={styles.settingInfo}>
                    <h3>Device Tracking</h3>
                    <p>Keep track of devices that have accessed your account</p>
                  </div>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox"
                      checked={securitySettings.deviceTracking}
                      onChange={() => handleSecurityChange('deviceTracking', !securitySettings.deviceTracking)}
                    />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>

              <div className={styles.securityActions}>
                <h3>Security Actions</h3>
                <div className={styles.actionButtons}>
                  <button 
                    className={styles.changePasswordButton}
                    onClick={handleChangePassword}
                  >
                    <i className="fas fa-key"></i>
                    Change Password
                  </button>
                  <button 
                    className={styles.viewHistoryButton}
                    onClick={handleViewLoginHistory}
                  >
                    <i className="fas fa-history"></i>
                    View Login History
                  </button>
                  <button 
                    className={styles.activeSessionsButton}
                    onClick={handleViewActiveSessions}
                  >
                    <i className="fas fa-devices"></i>
                    Manage Active Sessions
                  </button>
                  {/* <button 
                    className={styles.logoutAllButton}
                    onClick={handleLogoutAllDevices}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    Log Out All Devices
                  </button> */}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'usage' && !subscriptionLoading && (
            <div className={styles.section}>
              <UsageAnalytics />
            </div>
          )}

          {!subscriptionLoading && (
            <div className={styles.saveSection}>
              <button 
                className={styles.saveButton}
                onClick={handleSaveSettings}
                disabled={isLoading}
              >
              {isLoading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Save Settings
                </>
              )}
            </button>
            </div>
          )}
        </div>
      </div>

      <LoginHistory 
        isOpen={showLoginHistory} 
        onClose={() => setShowLoginHistory(false)} 
      />
      
      <ActiveSessions 
        isOpen={showActiveSessions} 
        onClose={() => setShowActiveSessions(false)} 
      />
      
      <TwoFactorSetup 
        isOpen={showTwoFactorSetup} 
        onClose={handleTwoFactorSetupComplete}
        currentMethod={twoFactorStatus.enabled ? twoFactorStatus.method : null}
      />
    </div>
  );
};

export default Settings; 